import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import { Order } from '../../types/cart';

interface OrderConfirmationProps {
  onBack: () => void;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { items, totalAmount, submitOrder } = useCart();
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState<Order | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const openModal = (modal: 'privacy' | 'terms') => {
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const legalContent = {
    privacy: {
      title: t('modals.privacy.title'),
      content: t('modals.privacy.content'),
    },
    terms: {
      title: t('modals.terms.title'),
      content: t('modals.terms.content'),
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const order = await submitOrder(customerInfo);
      setSubmittedOrder(order);
      setOrderSubmitted(true);
    } catch (error) {
      console.error('Error submitting order:', error);
      // Handle error - show error message
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSubmitted && submittedOrder) {
    return (
      <div className="min-h-screen bg-dark-primary py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Success Icon */}
            <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="font-playfair text-4xl text-cream mb-4">
              {t('order.successTitle')}
            </h1>
            <p className="text-cream-dark mb-8">
              {t('order.successDescription')}
            </p>

            <div className="bg-dark-surface border border-dark-border/30 rounded-lg p-8 mb-8 text-left">
              <h2 className="font-playfair text-2xl text-cream mb-4">{t('order.orderDetails')}</h2>
              <div className="space-y-2 mb-6">
                <p className="text-cream-dark">
                  <span className="text-cream">{t('order.orderNumber')}:</span> {submittedOrder.id}
                </p>
                <p className="text-cream-dark">
                  <span className="text-cream">{t('order.totalAmount')}:</span> €{submittedOrder.totalAmount}
                </p>
                <p className="text-cream-dark">
                  <span className="text-cream">{t('order.status')}:</span> {t(`order.status.${submittedOrder.status}`)}
                </p>
              </div>

              <h3 className="font-playfair text-lg text-cream mb-3">{t('order.items')}</h3>
              <div className="space-y-2 mb-6">
                {submittedOrder.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-cream-dark">
                      {item.imageTitle} - {item.size.name} × {item.quantity}
                    </span>
                    <span className="text-cream">€{item.price}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-playfair text-lg text-cream mb-3">{t('order.shippingInfo')}</h3>
              <div className="text-cream-dark text-sm space-y-1">
                <p>{submittedOrder.customerInfo.name}</p>
                <p>{submittedOrder.customerInfo.email}</p>
                <p>{submittedOrder.customerInfo.phone}</p>
                <p className="whitespace-pre-line">{submittedOrder.customerInfo.address}</p>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <a
                href="#preview-gallery"
                className="px-8 py-3 border border-gold/30 hover:border-gold hover:bg-gold/5 transition-all duration-300"
              >
                <span className="text-cream font-light tracking-wider uppercase text-sm">
                  {t('cart.continueShopping')}
                </span>
              </a>
              <a
                href="#order-history"
                className="px-8 py-3 bg-gold/90 hover:bg-gold transition-all duration-300"
              >
                <span className="text-dark-primary font-medium tracking-wider uppercase text-sm">
                  {t('order.viewOrders')}
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-primary py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl sm:text-5xl text-cream mb-4">
            {t('order.confirmTitle')}
          </h1>
          <div className="w-24 h-px bg-gold/50 mx-auto"></div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Customer Information Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-dark-surface border border-dark-border/30 rounded-lg p-8">
              <h2 className="font-playfair text-2xl text-cream mb-6">
                {t('order.customerInfo')}
              </h2>

              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-cream mb-2 font-light">
                    {t('order.name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-dark-primary border border-dark-border/30 text-cream rounded-lg focus:border-gold/50 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-cream mb-2 font-light">
                    {t('order.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-dark-primary border border-dark-border/30 text-cream rounded-lg focus:border-gold/50 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-cream mb-2 font-light">
                    {t('order.phone')} *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-dark-primary border border-dark-border/30 text-cream rounded-lg focus:border-gold/50 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-cream mb-2 font-light">
                    {t('order.address')} *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={customerInfo.address}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-dark-primary border border-dark-border/30 text-cream rounded-lg focus:border-gold/50 focus:outline-none resize-none"
                  />
                </div>

                <div>
                  <label htmlFor="notes" className="block text-cream mb-2 font-light">
                    {t('order.notes')}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-dark-primary border border-dark-border/30 text-cream rounded-lg focus:border-gold/50 focus:outline-none resize-none"
                  />
                </div>

                {/* Terms and Privacy Checkbox */}
                <div className="mt-8">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      required
                      className="mt-1 h-4 w-4 text-gold focus:ring-gold border-dark-border/30 rounded bg-dark-primary"
                    />
                    <label htmlFor="terms" className="text-cream-dark text-sm leading-relaxed">
                      {t('order.termsAgreement')}
                      <button 
                        onClick={() => openModal('privacy')}
                        className="text-gold hover:text-gold/80 underline mx-1"
                      >
                        {t('footer.privacy')}
                      </button>
                      {t('order.and')}
                      <button 
                        onClick={() => openModal('terms')}
                        className="text-gold hover:text-gold/80 underline mx-1"
                      >
                        {t('footer.terms')}
                      </button>
                      {t('order.termsRequired')} *
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <Button
                  type="button"
                  onClick={onBack}
                  variant="outline"
                  size="lg"
                >
                  {t('order.backToCart')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting || !termsAccepted}
                  className="flex-1"
                >
                  {isSubmitting ? t('order.submitting') : t('order.placeOrder')}
                </Button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-dark-surface border border-dark-border/30 rounded-lg p-6 sticky top-24">
              <h2 className="font-playfair text-2xl text-cream mb-6">
                {t('cart.orderSummary')}
              </h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-cream-dark">
                      {item.imageTitle} ({item.size.name} × {item.quantity})
                    </span>
                    <span className="text-cream">€{item.price}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-dark-border/20 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg text-cream">{t('cart.total')}</span>
                  <span className="text-3xl font-light text-gold">€{totalAmount}</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-dark-primary/50 rounded-lg">
                <p className="text-cream-dark text-sm">
                  {t('order.paymentNote')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      {activeModal && (
        <Modal
          isOpen={!!activeModal}
          onClose={closeModal}
          title={legalContent[activeModal].title}
          maxWidth="max-w-4xl"
        >
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{
              __html: legalContent[activeModal].content,
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default OrderConfirmation;